export type GhmTopicKind =
  | "meta"
  | "state"
  | "eventos"
  | "telemetria"
  | "sync";

export type GhmFunctionMode =
  | "DI"
  | "CT"
  | "PM"
  | "HSC"
  | "LATCH"
  | "RST"
  /** Leitura analogica escalada (ADS1220): `v`, `s` NE43 e `acc` opcional. */
  | "AIN"
  /**
   * Leitura Modbus RTU: `v`, `acc` opcional e `s` de COMUNICACAO
   * (ver GhmModbusStatus) — nao confundir com o `s` NE43 do AIN, que fala
   * sobre a faixa eletrica do sinal. Aqui `s` fala sobre o barramento.
   */
  | "MB";

/**
 * Semantica do campo `s` no modo MB. Diferente do NE43, nao ha "sobrefaixa":
 * ou a transacao fechou, ou nao ha valor novo.
 */
export const GHM_MODBUS_STATUS = {
  /** Nunca leu desde o boot da placa. */
  never: 0,
  ok: 1,
  /** Escravo mudo dentro da janela de resposta. */
  timeout: 2,
  /** CRC ou enquadramento invalido. */
  badFrame: 3,
  /** Escravo respondeu com codigo de excecao Modbus. */
  exception: 4,
} as const;

export type GhmModbusStatus =
  (typeof GHM_MODBUS_STATUS)[keyof typeof GHM_MODBUS_STATUS];

export const GHM_MODBUS_PORT_MIN = 6;
export const GHM_MODBUS_PORT_MAX = 21;

export interface GhmParsedTopic {
  prefix: string;
  mqttId: string;
  kind: GhmTopicKind;
  raw: string;
}

export interface GhmEnvelopePoint {
  p: number;
  m: GhmFunctionMode | string;
  v?: number;
  acc?: number;
  /**
   * Em AIN: 0=falha, 1=abaixo, 2=normal, 3=acima (NE43).
   * Em MB: status da comunicação (GHM_MODBUS_STATUS).
   * Demais modos mantêm sua semântica.
   */
  s: number;
}

export interface GhmDigitalInputDiagnostic {
  p: number;
  address: string;
  gpio: number;
  level: number;
  active: number;
  polarity?: string;
}

export interface GhmEnvelope {
  dev: string;
  ts: number | null;
  data: GhmEnvelopePoint[];
  digitalInputs?: GhmDigitalInputDiagnostic[];
}

/**
 * Resultado da leitura tolerante de um envelope. A estrutura externa continua
 * estrita, mas uma linha de ponto defeituosa nao invalida as demais leituras
 * validas do mesmo ciclo.
 */
export interface GhmEnvelopeParseResult<
  TPayload extends GhmEnvelope = GhmEnvelope,
> {
  payload: TPayload;
  rejectedPointCount: number;
}

/**
 * Semantica declarada de um valor. Ver `SignalKind` no configurador.
 *
 * O envelope tem tres slots e dois ja tem significado fechado: `acc` e sempre
 * totalizador monotonico, `s` e sempre estado ou diagnostico. Toda a
 * ambiguidade mora no `v` de AIN/MB, que pode ser qualquer grandeza.
 */
export const GHM_SIGNAL_KINDS = [
  "instantaneous",
  "rate",
  "cumulative",
  "state",
] as const;

export type GhmSignalKind = (typeof GHM_SIGNAL_KINDS)[number];

export function isGhmSignalKind(value: unknown): value is GhmSignalKind {
  return (
    typeof value === "string" &&
    (GHM_SIGNAL_KINDS as readonly string[]).includes(value)
  );
}

export interface GhmMetaFunctionValueDescriptor {
  label: string;
  unit?: string;
  /**
   * Ausente = o default deduzivel de `(modo, sinal)`. O firmware so emite
   * quando DIVERGE, porque declarar o obvio custaria ~20 B por descritor num
   * `/meta` que ja disputa 7680 B uteis numa configuracao de 22 portas.
   */
  kind?: GhmSignalKind;
  /** Casas decimais declaradas pelo configurador (0..3). */
  decimals?: number;
  /** Faixa de engenharia. So o analogico declara — Modbus nao tem span. */
  min?: number;
  max?: number;
}

/**
 * `kind` efetivo de um sinal: o declarado no `/meta` ou o default deduzivel do
 * par `(modo, sinal)`.
 *
 *  - `acc` e sempre acumulado, em qualquer modo;
 *  - `s` e sempre estado/diagnostico;
 *  - `v` em CT/PM/HSC e sempre taxa (vem de pulso por unidade de tempo);
 *  - `v` em AIN/MB e instantaneo ate que o `/meta` diga outra coisa.
 */
export function resolveSignalKind(
  mode: string | undefined,
  signal: "v" | "acc" | "s",
  // `unknown` de proposito: o descritor vem de JSON de rede. Validar aqui e o
  // trabalho desta funcao, entao exigir o tipo estreito na entrada empurraria
  // a validacao para cada chamador — que foi o padrao que criou o problema.
  descriptor?: { kind?: unknown } | null,
): GhmSignalKind {
  if (isGhmSignalKind(descriptor?.kind)) {
    return descriptor.kind;
  }
  if (signal === "acc") return "cumulative";
  if (signal === "s") return "state";

  const normalized = String(mode ?? "").trim().toUpperCase();
  if (normalized === "CT" || normalized === "PM" || normalized === "HSC") {
    return "rate";
  }
  return "instantaneous";
}

export interface GhmMetaFunctionDescriptor {
  p: number;
  m: GhmFunctionMode | string;
  id: string;
  label: string;
  enabled: boolean;
  source?: string;
  sourceB?: string;
  values?: Record<string, GhmMetaFunctionValueDescriptor>;
}

export interface GhmMetaPayload {
  dev: string;
  schema: string;
  firmwareVersion: string;
  configSchema: number;
  configHash: number;
  configHashHex: string;
  identity: {
    model?: string;
    friendlyName?: string;
    serialNumber?: string;
    mqttId?: string;
    mac?: string;
  };
  cadence: {
    telemetryMs: number;
    syncMs: number;
    events: string;
  };
  functions: GhmMetaFunctionDescriptor[];
}

export interface GhmStatePayload {
  value: "online" | "offline";
}

export interface GhmSyncPayload extends GhmEnvelope {
  uptimeMs?: number;
  deviceUptimeSec?: number;
  ip?: string;
  rssi?: number;
  firmwareVersion?: string;
  configSchema?: number;
  configHash?: number;
  configHashHex?: string;
}

export interface GhmEventPayload extends GhmEnvelope {}

export interface GhmTelemetryPayload extends GhmEnvelope {}

export interface GhmNormalizedEnvelope<TPayload extends GhmEnvelope = GhmEnvelope> {
  receivedAt: Date;
  measuredAt: Date;
  usedReceptionTimestamp: boolean;
  payload: TPayload;
}

const TOPIC_PATTERN = /^([^/]+)\/([^/]+)\/(meta|state|eventos|telemetria|sync)$/;
const CONFIG_HASH_HEX_PATTERN = /^0x[0-9A-F]{8}$/;

export function parseGhmTopic(topic: string): GhmParsedTopic | null {
  const match = TOPIC_PATTERN.exec(topic.trim());
  if (!match) {
    return null;
  }

  const [, prefix, mqttId, kind] = match;
  return {
    prefix,
    mqttId,
    kind: kind as GhmTopicKind,
    raw: topic,
  };
}

export function isGhmTopicKind(value: unknown): value is GhmTopicKind {
  return (
    value === "meta" ||
    value === "state" ||
    value === "eventos" ||
    value === "telemetria" ||
    value === "sync"
  );
}

export function isGhmFunctionMode(value: unknown): value is GhmFunctionMode {
  return (
    value === "DI" ||
    value === "CT" ||
    value === "PM" ||
    value === "HSC" ||
    value === "LATCH" ||
    value === "RST" ||
    value === "AIN" ||
    value === "MB"
  );
}

export function isValidConfigHashHex(value: unknown): value is string {
  return typeof value === "string" && CONFIG_HASH_HEX_PATTERN.test(value);
}

export function assertValidConfigHashHex(value: unknown): string {
  if (!isValidConfigHashHex(value)) {
    throw new Error("Invalid configHashHex. Expected format 0xFFFFFFFF.");
  }

  return value;
}

/**
 * Formats a 32-bit config hash number as the canonical `0xXXXXXXXX` string,
 * matching the firmware's `snprintf("0x%08lX")`. The firmware `/sync` envelope
 * carries `configHash` as a decimal number (not `configHashHex`), so consumers
 * must derive the hex form from it.
 */
export function configHashHexFromNumber(value: number): string | null {
  if (!Number.isFinite(value)) {
    return null;
  }

  const unsigned = value >>> 0;
  return `0x${unsigned.toString(16).toUpperCase().padStart(8, "0")}`;
}

/**
 * Resolves the canonical `configHashHex` from an envelope that may carry
 * either `configHashHex` (e.g. `/meta`) or only the decimal `configHash`
 * (e.g. `/sync`). Returns `null` when neither is usable.
 */
export function resolveConfigHashHex(source: {
  configHashHex?: unknown;
  configHash?: unknown;
}): string | null {
  if (isValidConfigHashHex(source.configHashHex)) {
    return source.configHashHex;
  }

  if (typeof source.configHash === "number") {
    return configHashHexFromNumber(source.configHash);
  }

  return null;
}

export function parseStatePayload(raw: string): GhmStatePayload | null {
  const value = raw.trim();
  if (value !== "online" && value !== "offline") {
    return null;
  }

  return { value };
}

export function tryParseJson<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function isGhmEnvelope(value: unknown): value is GhmEnvelope {
  const parsed = parseGhmEnvelopePayload(value);
  return parsed !== null && parsed.rejectedPointCount === 0;
}

/**
 * Faz parse defensivo do envelope sem transformar uma falha localizada em
 * perda do pacote inteiro. Se havia pontos e nenhum deles e valido, o pacote
 * continua rejeitado para nao produzir falso sinal de vida.
 */
export function parseGhmEnvelopePayload<
  TPayload extends GhmEnvelope = GhmEnvelope,
>(value: unknown): GhmEnvelopeParseResult<TPayload> | null {
  if (!isRecord(value)) {
    return null;
  }

  if (typeof value.dev !== "string") {
    return null;
  }

  if (!(isFiniteInteger(value.ts) || value.ts === null)) {
    return null;
  }

  if (!Array.isArray(value.data)) {
    return null;
  }

  if (
    value.digitalInputs !== undefined &&
    (!Array.isArray(value.digitalInputs) ||
      !value.digitalInputs.every(isGhmDigitalInputDiagnostic))
  ) {
    return null;
  }

  const validPoints = value.data.filter(isGhmEnvelopePoint);
  const rejectedPointCount = value.data.length - validPoints.length;
  if (value.data.length > 0 && validPoints.length === 0) {
    return null;
  }

  return {
    payload: { ...value, data: validPoints } as unknown as TPayload,
    rejectedPointCount,
  };
}

export function isGhmEnvelopePoint(value: unknown): value is GhmEnvelopePoint {
  if (!isRecord(value)) {
    return false;
  }

  if (!isNonNegativeInteger(value.p)) {
    return false;
  }

  if (typeof value.m !== "string") {
    return false;
  }

  if (!isFiniteInteger(value.s)) {
    return false;
  }

  if (value.v !== undefined && !isFiniteNumber(value.v)) {
    return false;
  }

  if (value.acc !== undefined && !isFiniteNumber(value.acc)) {
    return false;
  }

  if (value.m === "MB") {
    if (value.p < GHM_MODBUS_PORT_MIN || value.p > GHM_MODBUS_PORT_MAX) {
      return false;
    }

    if (value.s < GHM_MODBUS_STATUS.never || value.s > GHM_MODBUS_STATUS.exception) {
      return false;
    }

    // Falha de comunicacao nunca carrega valor instantaneo: isso impede que
    // um ultimo valor bom seja reinterpretado como leitura atual.
    if (value.s !== GHM_MODBUS_STATUS.ok && value.v !== undefined) {
      return false;
    }
  }

  return true;
}

export function isGhmDigitalInputDiagnostic(
  value: unknown,
): value is GhmDigitalInputDiagnostic {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isNonNegativeInteger(value.p) &&
    typeof value.address === "string" &&
    isNonNegativeInteger(value.gpio) &&
    isBinaryInteger(value.level) &&
    isBinaryInteger(value.active) &&
    (value.polarity === undefined || typeof value.polarity === "string")
  );
}

export function isGhmMetaPayload(value: unknown): value is GhmMetaPayload {
  if (!isRecord(value)) {
    return false;
  }

  if (
    typeof value.dev !== "string" ||
    typeof value.schema !== "string" ||
    typeof value.firmwareVersion !== "string" ||
    !isNonNegativeInteger(value.configSchema) ||
    !isFiniteInteger(value.configHash) ||
    !isValidConfigHashHex(value.configHashHex)
  ) {
    return false;
  }

  if (!isRecord(value.identity) || !isRecord(value.cadence)) {
    return false;
  }

  if (
    !isPositiveInteger(value.cadence.telemetryMs) ||
    !isPositiveInteger(value.cadence.syncMs) ||
    typeof value.cadence.events !== "string"
  ) {
    return false;
  }

  if (!Array.isArray(value.functions)) {
    return false;
  }

  return value.functions.every(isGhmMetaFunctionDescriptor);
}

export function isGhmMetaFunctionDescriptor(
  value: unknown,
): value is GhmMetaFunctionDescriptor {
  if (!isRecord(value)) {
    return false;
  }

  if (
    !isNonNegativeInteger(value.p) ||
    typeof value.m !== "string" ||
    typeof value.id !== "string" ||
    typeof value.label !== "string" ||
    typeof value.enabled !== "boolean"
  ) {
    return false;
  }

  if (value.source !== undefined && typeof value.source !== "string") {
    return false;
  }

  if (value.sourceB !== undefined && typeof value.sourceB !== "string") {
    return false;
  }

  if (value.values !== undefined && !isMetaValuesRecord(value.values)) {
    return false;
  }

  return true;
}

export function normalizeEnvelopeTimestamp<TPayload extends GhmEnvelope>(
  payload: TPayload,
  receivedAt: Date = new Date(),
): GhmNormalizedEnvelope<TPayload> {
  const measuredAt =
    payload.ts === null
      ? new Date(receivedAt)
      : new Date(payload.ts * 1000);

  return {
    receivedAt,
    measuredAt,
    usedReceptionTimestamp: payload.ts === null,
    payload,
  };
}

export function getPointKey(point: Pick<GhmEnvelopePoint, "p" | "m">): string {
  return `${point.p}:${point.m}`;
}

function isMetaValuesRecord(
  value: unknown,
): value is Record<string, GhmMetaFunctionValueDescriptor> {
  if (!isRecord(value)) {
    return false;
  }

  return Object.values(value).every((item) => {
    if (!isRecord(item)) {
      return false;
    }

    if (typeof item.label !== "string") {
      return false;
    }

    if (item.unit !== undefined && typeof item.unit !== "string") {
      return false;
    }

    // `kind` desconhecido nao invalida o `/meta`: um firmware mais novo pode
    // declarar uma semantica que este worker ainda nao entende, e derrubar o
    // pacote inteiro deixaria o dashboard sem descobrir o equipamento. O
    // consumidor cai no default deduzido (`resolveSignalKind`).
    if (item.kind !== undefined && typeof item.kind !== "string") {
      return false;
    }

    for (const numeric of ["decimals", "min", "max"] as const) {
      if (item[numeric] !== undefined && !isFiniteNumber(item[numeric])) {
        return false;
      }
    }

    return true;
  });
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isFiniteInteger(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isInteger(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return isFiniteInteger(value) && value >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return isFiniteInteger(value) && value > 0;
}

function isBinaryInteger(value: unknown): value is 0 | 1 {
  return value === 0 || value === 1;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
